/**
 * The team on screen's shopping list and its purchase log.
 *
 * Kept out of the board store because none of it is tasks. Fetched over REST
 * when the Shopping view opens (it is not in the socket's snapshot, like the
 * calendar) and kept live by the `shoppingItem*` / `purchase*` events, which
 * are only ever about the one team whose list is held.
 */
import { computed, reactive, ref } from "vue";
import { defineStore } from "pinia";
import { api } from "../lib/api.js";
import type { Purchase, ServerEvent, ShoppingItem } from "@shared/types.js";

export const useShopping = defineStore("shopping", () => {
  /** The team whose list is held, or null before the view has been opened. */
  const teamId = ref<string | null>(null);
  const items = reactive(new Map<string, ShoppingItem>());
  /** The newest part of the team's log, plus anything logged since. */
  const purchases = reactive(new Map<string, Purchase>());
  const loaded = ref(false);

  const byName = (a: ShoppingItem, b: ShoppingItem): number =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  /** What is on the list, then everything else — each by name. */
  const needed = computed(() => [...items.values()].filter((i) => i.needed).sort(byName));
  const stocked = computed(() => [...items.values()].filter((i) => !i.needed).sort(byName));
  const log = computed(() => [...purchases.values()].sort((a, b) => b.boughtAt - a.boughtAt || b.id.localeCompare(a.id)));

  let request = 0;

  /**
   * Whether a purchase goes in the held log: a new one, or one already there.
   * A price corrected on a purchase older than the page held must not wedge
   * itself into the log with a gap in front of it.
   */
  function belongs(p: Purchase): boolean {
    if (purchases.has(p.id) || purchases.size === 0) return true;
    const oldest = log.value[log.value.length - 1]!;
    return p.boughtAt >= oldest.boughtAt;
  }

  /** Fetch a team's list, replacing what is held. */
  async function load(team: string): Promise<void> {
    const mine = ++request;
    if (team !== teamId.value) {
      teamId.value = team;
      items.clear();
      purchases.clear();
      loaded.value = false;
    }
    const page = await api.get<{ items: ShoppingItem[]; purchases: Purchase[] }>(
      `/api/shopping?team=${encodeURIComponent(team)}`,
    );
    // A slower answer for a team since switched away from must not land.
    if (mine !== request) return;
    items.clear();
    for (const item of page.items) items.set(item.id, item);
    purchases.clear();
    for (const p of page.purchases) purchases.set(p.id, p);
    loaded.value = true;
  }

  function apply(event: ServerEvent): void {
    if (!loaded.value) return;
    switch (event.t) {
      case "shoppingItemUpsert":
        if (event.item.teamId === teamId.value) items.set(event.item.id, event.item);
        break;
      case "shoppingItemDelete":
        items.delete(event.id);
        for (const p of purchases.values()) if (p.itemId === event.id) purchases.delete(p.id);
        break;
      case "purchaseUpsert":
        if (event.purchase.teamId === teamId.value && belongs(event.purchase)) purchases.set(event.purchase.id, event.purchase);
        break;
      case "purchaseDelete":
        purchases.delete(event.id);
        break;
    }
  }

  /**
   * After a gap the change log could not cover, or the socket coming back.
   * Refetched rather than trusted to the replay: a delete leaves nothing
   * behind to replay if it fell out of the retained log.
   */
  function resync(): void {
    if (teamId.value && loaded.value) void load(teamId.value).catch(() => undefined);
  }

  function reset(): void {
    request++;
    teamId.value = null;
    items.clear();
    purchases.clear();
    loaded.value = false;
  }

  /**
   * The item as a reply to one of our writes has it. The echo of the same write
   * comes over the socket too, and so may somebody else's change made after it,
   * or their delete: a reply is not taken over a newer row, or once the item is gone.
   */
  function replied(item: ShoppingItem): void {
    const held = items.get(item.id);
    if (held && held.updatedAt <= item.updatedAt) items.set(item.id, item);
  }

  // ── writes (the echo also arrives over the socket; both are idempotent) ──
  async function add(name: string): Promise<ShoppingItem> {
    const { item } = await api.post<{ item: ShoppingItem }>("/api/shopping/items", { teamId: teamId.value, name });
    items.set(item.id, item);
    return item;
  }

  async function update(
    id: string,
    patch: { name?: string; needed?: boolean; priceMinor?: number | null; store?: string },
  ): Promise<void> {
    const { item } = await api.patch<{ item: ShoppingItem }>(`/api/shopping/items/${id}`, patch);
    replied(item);
  }

  async function buy(id: string): Promise<void> {
    const { item, purchase } = await api.post<{ item: ShoppingItem; purchase: Purchase }>(
      `/api/shopping/items/${id}/bought`,
      {},
    );
    replied(item);
    purchases.set(purchase.id, purchase);
  }

  async function remove(id: string): Promise<void> {
    await api.del(`/api/shopping/items/${id}`);
    apply({ t: "shoppingItemDelete", seq: 0, id, teamId: teamId.value ?? "" });
  }

  /** Correct what one purchase cost, or where it was from. */
  async function updatePurchase(id: string, patch: { priceMinor?: number | null; store?: string }): Promise<Purchase> {
    const { purchase } = await api.patch<{ purchase: Purchase }>(`/api/shopping/purchases/${id}`, patch);
    if (belongs(purchase)) purchases.set(purchase.id, purchase);
    return purchase;
  }

  async function removePurchase(id: string): Promise<void> {
    const { item } = await api.del<{ item: ShoppingItem }>(`/api/shopping/purchases/${id}`);
    purchases.delete(id);
    replied(item);
  }

  /** Every store named on the list, for the store box to suggest. */
  const stores = computed(() =>
    [...new Set([...items.values()].map((i) => i.store).concat([...purchases.values()].map((p) => p.store)))]
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })),
  );

  /** One item's whole history — the team log only holds the newest part. */
  async function history(id: string): Promise<Purchase[]> {
    return (await api.get<{ purchases: Purchase[] }>(`/api/shopping/items/${id}/purchases`)).purchases;
  }

  return {
    teamId, items, purchases, loaded, needed, stocked, log, stores,
    load, apply, resync, reset, add, update, buy, remove, updatePurchase, removePurchase, history,
  };
});
